import React, { useState, useEffect, useRef, useMemo } from 'react';
import './ServicesRipple.css';
import useMobileBreakpoint from "@arctheme-hooks/useMobileBreakpoint";

// Define types for our service items
interface ServiceItem {
  id: number;
  title: string;
  link: string;
  x: number;
  y: number;
  initialX: number;
  initialY: number;
  radius: number; 
  animationOffset: number;
}

interface ServiceBlockSettings {
  title: string;
  url: string;
  radius: number;
  radius_mobile?: number;
  animation_offset?: number;
}

interface ServicesRippleProps {
  settings: {
    settings: {
      section: {
        title?: string;
        ripple_count?: number;
        ripple_spacing?: number;
        ripple_color?: string;
      };
      blocks: Array<{
        type: string;
        settings: ServiceBlockSettings;
      }>;
      mobile_breakpoint?: number;
    };
  };
}

export const ServicesRipple: React.FC<ServicesRippleProps> = ({ settings }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [services, setServices] = useState<ServiceItem[]>([]);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [isHovering, setIsHovering] = useState(false);
  const animationFrameRef = useRef<number>();
  
  // Extract settings with reasonable defaults
  const { 
    title = "Foundations", 
    ripple_count = 6, 
    ripple_spacing = 150,
    ripple_spacing_mobile = 100,
    ripple_color = "#899B30",
  } = settings?.settings?.section || {};
  
  // Use the mobile breakpoint hook to determine if we're on mobile
  const isMobile = useMobileBreakpoint(settings.settings.mobile_breakpoint);
  
  // Extract service items from blocks and memoize to prevent unnecessary recalculations
  const serviceBlocks = useMemo(() => {
    return settings?.settings?.blocks?.filter(block => block.type === "service_item") || [];
  }, [settings?.settings?.blocks]);
  
  // Concentric circle positioning logic
  useEffect(() => {
    // Define the position calculation function
    const calculatePositions = () => {
      if (!containerRef.current) return;
      
      const container = containerRef.current;
      const rect = container.getBoundingClientRect();
      
      // Define center point
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      
      // Map service blocks to positions
      const positions = serviceBlocks.map((block, index) => {
        // Use mobile radius if on mobile and it's defined, otherwise use desktop radius or fallback
        const radius = isMobile && block.settings.radius_mobile !== undefined 
          ? block.settings.radius_mobile 
          : block.settings.radius || 250 + (index * 50);
        
        return {
          id: index + 1,
          title: block.settings.title,
          link: block.settings.url || '#',
          radius: radius,
          animationOffset: block.settings.animation_offset !== undefined 
            ? block.settings.animation_offset 
            : index * 1.5
        };
      });
      
      // If no service blocks are defined, use default values
      const mappedPositions = (positions.length > 0 ? positions : [
        { id: 1, title: "Connect", link: "#strategy", radius: isMobile ? 150 : 250, animationOffset: 0 },
        { id: 2, title: "Design", link: "#branding", radius: isMobile ? 200 : 350, animationOffset: 1.5 },
        { id: 3, title: "Transform", link: "#digital", radius: isMobile ? 200 : 350, animationOffset: 3 },
      ]).map(item => {
        // For concentric circles, we place all services at different positions along their orbits
        const angle = (item.id * 100) % 360 * Math.PI / 180; // Distribute around the circle
        
        // Calculate position using radius and angle
        const posX = centerX + item.radius * Math.cos(angle);
        const posY = centerY + item.radius * Math.sin(angle);
        
        // Account for element size (150px width/height)
        const x = posX - 75; // 75 is half width
        const y = posY - 75; // 75 is half height
        
        return {
          ...item,
          x,
          y,
          initialX: x,
          initialY: y
        };
      });
      
      setContainerSize({ width: rect.width, height: rect.height });
      setServices(mappedPositions);
    };
    
    // Initial calculation
    calculatePositions();
    
    // Add resize listener
    window.addEventListener('resize', calculatePositions);
    
    // Cleanup resize listener
    return () => {
      window.removeEventListener('resize', calculatePositions);
    };
  }, [serviceBlocks, isMobile]); // Add isMobile to dependencies
    
  // Add floating animation to service circles
  useEffect(() => {
    // Animation loop for subtle floating movement
    const animateServiceCircles = () => {
      if (isHovering) {
        // Don't apply ambient animation during mouse interaction
        animationFrameRef.current = requestAnimationFrame(animateServiceCircles);
        return;
      }
      
      setServices(prev => prev.map(service => {
        // Create subtle random movement based on time
        const time = Date.now() / 1000 + service.animationOffset;
        const floatX = Math.sin(time * 0.5) * 5; // Horizontal movement (5px range)
        const floatY = Math.cos(time * 0.7) * 5; // Vertical movement (5px range)
        
        return {
          ...service,
          x: service.initialX + floatX,
          y: service.initialY + floatY
        };
      }));
      
      animationFrameRef.current = requestAnimationFrame(animateServiceCircles);
    };
    
    // Start animation
    animationFrameRef.current = requestAnimationFrame(animateServiceCircles);
    
    // Cleanup animation frame on component unmount
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isHovering, services.length]); // Depend on isHovering and services.length
  
  // Handle mouse movement
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!containerRef.current) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    setMousePosition({ x, y });
    setIsHovering(true);
    
    // Update service positions based on mouse proximity
    setServices(prev => prev.map(service => {
      // Calculate service center
      const serviceCenterX = service.initialX + 75;
      const serviceCenterY = service.initialY + 75;
      
      // Calculate distance from mouse to service center
      const dx = x - serviceCenterX;
      const dy = y - serviceCenterY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      // Pull effect inversely proportional to distance
      const pullStrength = 30 + service.radius / 20;
      const maxDistance = 250;
      let pull = 0;
      
      if (distance < maxDistance) {
        pull = pullStrength * (1 - distance / maxDistance);
      }
      
      // Direction vector
      const dirX = distance === 0 ? 0 : dx / distance;
      const dirY = distance === 0 ? 0 : dy / distance;
      
      // New position
      const newX = service.initialX + dirX * pull;
      const newY = service.initialY + dirY * pull;
      
      return {
        ...service,
        x: newX,
        y: newY
      };
    }));
  };
  
  const handleMouseLeave = () => {
    setIsHovering(false);
  };

  // Generate ripple circles based on settings
  const rippleCircles = useMemo(() => {
    // Use mobile spacing value when on mobile devices
    const spacing = isMobile ? ripple_spacing_mobile : ripple_spacing;
    
    return Array.from(
      { length: ripple_count }, 
      (_, i) => 150 + i * spacing
    );
  }, [ripple_count, ripple_spacing, ripple_spacing_mobile, isMobile]);

  return (
    <div 
      className="services-container"
      ref={containerRef} 
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <div className="absolute bottom-0 left-4 lg:left-16 w-[163px] lg:w-[267px]">
        <img className={`absolute bottom-0 left-0 w-full`} src="https://cdn.shopify.com/s/files/1/1987/3449/files/shopify-platinum-partner-badge_1.png?v=1740573912" />
      </div>
      {/* Ripple circles */}
      <svg className="svg-container ripple-container absolute">
        {rippleCircles.map((radius, index) => (
          <circle
            key={`ripple-${index}`}
            cx={containerSize.width / 2}
            cy={containerSize.height / 2}
            r={radius}
            className="ripple-circle"
            strokeDasharray="1"
            style={{ stroke: ripple_color }}
          />
        ))}
      </svg>
      
      {/* Center text */}
      <div className="center-text-container">
        <span className="center-main-text">{title}</span>
      </div>
      
      {/* Service circles */}
      {services.map(service => (
        <div 
          key={service.id} 
          className="service-circle"
          style={{
            transform: `translate(${service.x}px, ${service.y}px)`,
            transition: isHovering ? 'transform 0.3s ease-out' : 'none'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = `translate(${service.x}px, ${service.y}px) scale(1.05)`;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = `translate(${service.x}px, ${service.y}px)`;
          }}
        >
          <a href={service.link} className="service-link">
            {service.title}
          </a>
        </div>
      ))}
    </div>
  );
};

export default ServicesRipple;
